import React, { useState, useMemo } from "react";
import {
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Typography,
    Card,
    CardMedia,
    Button,
    alpha,
    useMediaQuery,
} from "@mui/material";
import {
    Close as CloseIcon,
    Image as ImageIcon
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { api } from "../../config";

const themeColors = {
    primary: "#99191b",
    secondary: "#e3764c"
};
const BG_DETAILS = "#eef2f4";
const MAX_WORDS = 250;

const ProjectModal = ({ project, isOpen, onClose }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [showFullDesc, setShowFullDesc] = useState(false);
    const { t } = useTranslation();

    // ===== Helpers =====
    const getGalleryImages = () => {
        if (!project) return [];
        if (Array.isArray(project.images) && project.images.length) {
            return [...project.images]
                .sort((a, b) => (a.isHero ? -1 : b.isHero ? 1 : 0))
                .map((img) => ({ path: img.path || img, isHero: !!img.isHero }));
        }
        if (project?.image) return [{ path: project.image, isHero: true }];
        if (project?.immagine1) return [{ path: project.immagine1, isHero: true }];
        return [];
    };
    const galleryImages = getGalleryImages();

    const descriptionParts = useMemo(() => {
        const text = project?.descrizione || "";
        const words = text.trim().split(/\s+/);
        if (!text) return { short: "", long: "", needsToggle: false };
        if (words.length <= MAX_WORDS)
            return { short: text, long: text, needsToggle: false };
        return {
            short: words.slice(0, MAX_WORDS).join(" ") + "\u2026",
            long: text,
            needsToggle: true
        };
    }, [project?.descrizione]);

    const getCategories = () => {
        if (project?.categorie_nomi && Array.isArray(project.categorie_nomi)) {
            return project.categorie_nomi;
        }
        if (project?.category) {
            return [project.category];
        }
        return [];
    };

    const categories = getCategories();

    if (!isOpen || !project) return null;

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            fullScreen={isMobile}
            maxWidth="lg"
            fullWidth
            scroll="paper"
            PaperProps={{
                elevation: 12,
                sx: {
                    borderRadius: isMobile ? 0 : 4,
                    boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
                    overflow: "hidden",
                    bgcolor: BG_DETAILS,
                    maxHeight: "95vh"
                }
            }}
        >
            <DialogTitle
                sx={{
                    m: 0,
                    p: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    borderBottom: "none",
                    bgcolor: BG_DETAILS
                }}
            >
                <IconButton
                    aria-label={t('common.close')}
                    onClick={onClose}
                    sx={{ color: "text.primary", "&:hover": { backgroundColor: "action.hover" } }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent
                sx={{
                    p: 0,
                    maxHeight: { xs: "calc(90vh - 56px)", sm: "calc(90vh - 96px)" },
                    overflow: "auto"
                }}
                dividers={false}
            >
                <Box sx={{ bgcolor: BG_DETAILS }}>
                    <Box
                        sx={{
                            mx: "auto",
                            maxWidth: 1100,
                            px: { xs: 2, sm: 3, md: 4 },
                            py: { xs: 2, sm: 3 }
                        }}
                    >
                        {/* Grid compatta */}
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr", md: "minmax(280px, 1fr) 1fr" },
                                gap: { xs: 2, sm: 3 },
                                alignItems: "start"
                            }}
                        >
                            {/* Galleria immagini */}
                            <Box sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1.5
                            }}>
                                {(galleryImages.length ? galleryImages : [{ path: null }]).map((img, i) => (
                                    <Card
                                        key={i}
                                        sx={{
                                            borderRadius: 2,
                                            overflow: "hidden",
                                            bgcolor: img.path ? "transparent" : "grey.50",
                                            border: img.path ? "none" : "2px dashed",
                                            borderColor: "grey.300"
                                        }}
                                    >
                                        {img.path ? (
                                            <CardMedia
                                                component="img"
                                                image={
                                                    img.path.startsWith("http") || img.path.startsWith("/")
                                                        ? img.path
                                                        : api(`/Progetti/${project.id}/${img.path}`)
                                                }
                                                alt={`img-${i}`}
                                                sx={{
                                                    width: "100%",
                                                    aspectRatio: "4 / 3",
                                                    objectFit: "cover",
                                                    display: "block"
                                                }}
                                            />
                                        ) : (
                                            <Box
                                                sx={{
                                                    p: 2,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    aspectRatio: "4 / 3"
                                                }}
                                            >
                                                <ImageIcon sx={{ fontSize: 40, color: "grey.400" }} />
                                            </Box>
                                        )}
                                    </Card>
                                ))}
                            </Box>

                            {/* Contenuto testi */}
                            <Box sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                                textAlign: "right"
                            }}>
                                {/* Titolo */}
                                <Box sx={{ mb: 0.5, maxWidth: 560 }}>
                                    <Typography
                                        fontWeight={700}
                                        sx={{
                                            fontSize: { xs: "1.8rem", sm: "2.2rem", md: "2.5rem" },
                                            lineHeight: 1.02,
                                            wordBreak: "break-word",
                                            fontFamily: "Roboto, sans-serif",
                                            letterSpacing: "-0.03em"
                                        }}
                                    >
                                        {project.titolo}
                                    </Typography>
                                    {project.sottotitolo && (
                                        <Typography
                                            fontWeight={700}
                                            sx={{
                                                mt: 0.3,
                                                fontSize: { xs: "1.1rem", sm: "1.3rem", md: "1.5rem" },
                                                lineHeight: 1.05,
                                                wordBreak: "break-word",
                                                fontFamily: "Roboto, sans-serif",
                                                letterSpacing: "-0.03em"
                                            }}
                                        >
                                            {project.sottotitolo}
                                        </Typography>
                                    )}
                                </Box>

                                {/* Metadati */}
                                <Box sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1
                                }}>
                                    <Box>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                display: "block",
                                                fontWeight: 700,
                                                color: themeColors.primary,
                                                fontSize: "0.78rem",
                                                fontFamily: "Roboto, sans-serif",
                                                textTransform: "uppercase",
                                                letterSpacing: "0.08em",
                                                textAlign: "right"
                                            }}
                                        >
                                            {t('modal.location')}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{ fontFamily: "Roboto, sans-serif", fontWeight: 300, fontSize: "1rem", lineHeight: 1.45 }}
                                        >
                                            {project.citta || project.luogo || project.city || "\u2014"}
                                        </Typography>
                                    </Box>

                                    <Box>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                display: "block",
                                                fontWeight: 700,
                                                color: themeColors.primary,
                                                fontSize: "0.78rem",
                                                fontFamily: "Roboto, sans-serif",
                                                textTransform: "uppercase",
                                                letterSpacing: "0.08em",
                                                textAlign: "right"
                                            }}
                                        >
                                            {t('modal.year')}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{ fontFamily: "Roboto, sans-serif", fontWeight: 300, fontSize: "1rem", lineHeight: 1.45 }}
                                        >
                                            {project.data_inizio || project.inizio || "\u2014"}
                                            {(project.data_fine || project.fine) && " \u2013 "}
                                            {project.data_fine || project.fine || ""}
                                        </Typography>
                                    </Box>

                                    {/* CATEGORIE MULTIPLE */}
                                    <Box>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                display: "block",
                                                fontWeight: 700,
                                                color: themeColors.primary,
                                                fontSize: "0.78rem",
                                                fontFamily: "Roboto, sans-serif",
                                                textTransform: "uppercase",
                                                letterSpacing: "0.08em",
                                                textAlign: "right"
                                            }}
                                        >
                                            {categories.length > 1 ? t('modal.typePlural') : t('modal.typeSingular')}
                                        </Typography>
                                        {categories.length > 0 ? (
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontFamily: "Roboto, sans-serif",
                                                    fontWeight: 300,
                                                    fontSize: "1rem",
                                                    lineHeight: 1.45
                                                }}
                                            >
                                                {categories.map(cat =>
                                                    cat === "PPP" ? "PPP" :
                                                        cat.charAt(0).toUpperCase() + cat.slice(1).toLowerCase()
                                                ).join(" - ")}
                                            </Typography>
                                        ) : (
                                            <Typography
                                                variant="body2"
                                                sx={{ fontFamily: "Roboto, sans-serif", fontWeight: 300, fontSize: "1rem", lineHeight: 1.45 }}
                                            >
                                                {"\u2014"}
                                            </Typography>
                                        )}
                                    </Box>

                                    {project.committente && (
                                        <Box>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    display: "block",
                                                    fontWeight: 700,
                                                    color: themeColors.primary,
                                                    fontSize: "0.78rem",
                                                    fontFamily: "Roboto, sans-serif",
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.08em"
                                                }}
                                            >
                                                {t('modal.client')}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{ fontFamily: "Roboto, sans-serif", fontWeight: 300, fontSize: "1rem", lineHeight: 1.45 }}
                                            >
                                                {project.committente}
                                            </Typography>
                                        </Box>
                                    )}

                                    {project.incarico && (
                                        <Box>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    display: "block",
                                                    fontWeight: 700,
                                                    color: themeColors.primary,
                                                    fontSize: "0.78rem",
                                                    fontFamily: "Roboto, sans-serif",
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.08em"
                                                }}
                                            >
                                                {t('modal.assignment')}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{ fontFamily: "Roboto, sans-serif", fontWeight: 300, fontSize: "1rem", lineHeight: 1.45 }}
                                            >
                                                {project.incarico}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>

                                {/* Descrizione */}
                                {project.descrizione && (
                                    <Box sx={{ mt: 0.5 }}>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                display: "block",
                                                fontWeight: 700,
                                                color: themeColors.primary,
                                                fontSize: "0.78rem",
                                                fontFamily: "Roboto, sans-serif",
                                                textTransform: "uppercase",
                                                letterSpacing: "0.08em",
                                                textAlign: "right",
                                                textAlign: "right",
                                                mb: 1
                                            }}
                                        >
                                            {t('modal.description')}
                                        </Typography>

                                        <Typography
                                            variant="body2"
                                            sx={{
                                                whiteSpace: "pre-line",
                                                wordBreak: "break-word",
                                                lineHeight: 1.7,
                                                textAlign: "justify",
                                                textAlignLast: "right",
                                                fontFamily: "Roboto, sans-serif",
                                                fontWeight: 300,
                                                fontSize: "1rem",
                                                maxWidth: 620
                                            }}
                                        >
                                            {showFullDesc ? descriptionParts.long : descriptionParts.short}
                                        </Typography>
                                        {descriptionParts.needsToggle && (
                                            <Box sx={{ mt: 1, textAlign: "right" }}>
                                                <Button
                                                    onClick={() => setShowFullDesc((v) => !v)}
                                                    size="small"
                                                    sx={{
                                                        borderRadius: 1.5,
                                                        px: 2,
                                                        py: 0.5,
                                                        fontWeight: 700,
                                                        fontSize: "0.8rem",
                                                        color: themeColors.primary,
                                                        "&:hover": { backgroundColor: alpha(themeColors.primary, 0.08) },
                                                        fontFamily: "Roboto, sans-serif"
                                                    }}
                                                >
                                                    {showFullDesc ? t('modal.showLess') : t('modal.showMore')}
                                                </Button>
                                            </Box>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        </Box>

                        {/* Footer */}
                        <Box
                            sx={{
                                mt: { xs: 2, sm: 2.5 },
                                pt: { xs: 1.5, sm: 2 },
                                borderTop: "1px solid",
                                borderColor: "divider"
                            }}
                        >
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                    fontSize: "0.7rem",
                                    fontFamily: "Roboto, sans-serif",
                                    fontWeight: 300,
                                    float: "right",
                                }}
                            >
                                {t('modal.copyright', { year: new Date().getFullYear() })}
                            </Typography>
                            <br/>
                        </Box>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default ProjectModal;
